// Small gradient illustrations for the experience cards, drawn in the site palette.
const Defs = ({ id }) => (
  <defs>
    <linearGradient id={`${id}-pink`} x1="0" y1="0" x2="1" y2="1">
      <stop offset="0%" stopColor="#ff8fb3" />
      <stop offset="100%" stopColor="#d4386b" />
    </linearGradient>
    <linearGradient id={`${id}-blush`} x1="0" y1="0" x2="0" y2="1">
      <stop offset="0%" stopColor="#fbe3ea" />
      <stop offset="100%" stopColor="#f2bfd0" />
    </linearGradient>
    <linearGradient id={`${id}-ink`} x1="0" y1="0" x2="1" y2="1">
      <stop offset="0%" stopColor="#3b2a2f" />
      <stop offset="100%" stopColor="#211d1b" />
    </linearGradient>
    <radialGradient id={`${id}-glow`} cx="50%" cy="50%" r="50%">
      <stop offset="0%" stopColor="#e8457a" stopOpacity="0.35" />
      <stop offset="100%" stopColor="#e8457a" stopOpacity="0" />
    </radialGradient>
  </defs>
);

const Frame = ({ id, children, className }) => (
  <svg viewBox="0 0 120 120" className={className} aria-hidden="true">
    <Defs id={id} />
    <circle cx="60" cy="62" r="54" fill={`url(#${id}-glow)`} />
    {children}
  </svg>
);

// Neural network: three layers of nodes wired together.
const Network = ({ className }) => {
  const id = "ill-net";
  const layers = [
    [30, [34, 60, 86]],
    [60, [24, 48, 72, 96]],
    [90, [44, 76]],
  ];
  const edges = [];
  for (let l = 0; l < layers.length - 1; l++) {
    const [x1, ys1] = layers[l];
    const [x2, ys2] = layers[l + 1];
    ys1.forEach((y1) => ys2.forEach((y2) => edges.push([x1, y1, x2, y2])));
  }
  return (
    <Frame id={id} className={className}>
      {edges.map(([x1, y1, x2, y2], i) => (
        <line key={i} x1={x1} y1={y1} x2={x2} y2={y2} stroke="#e8457a" strokeOpacity="0.35" strokeWidth="1.5" />
      ))}
      {layers.map(([x, ys], l) =>
        ys.map((y) => (
          <circle
            key={`${l}-${y}`}
            cx={x}
            cy={y}
            r={l === 1 ? 7 : 8}
            fill={l === 1 ? `url(#${id}-ink)` : `url(#${id}-pink)`}
            stroke="#f7ede4"
            strokeWidth="2"
          />
        ))
      )}
      <circle cx="90" cy="44" r="3" fill="#fff" opacity="0.8" />
    </Frame>
  );
};

// Chalkboard with a rising learning curve.
const Teach = ({ className }) => {
  const id = "ill-teach";
  return (
    <Frame id={id} className={className}>
      <rect x="14" y="22" width="92" height="64" rx="8" fill={`url(#${id}-ink)`} />
      <rect x="14" y="22" width="92" height="64" rx="8" fill="none" stroke="#f2bfd0" strokeWidth="3" />
      <path d="M26 72 C 42 70, 50 60, 60 52 S 82 34, 94 32" fill="none" stroke={`url(#${id}-pink)`} strokeWidth="4" strokeLinecap="round" />
      <circle cx="94" cy="32" r="4" fill="#ff8fb3" />
      <line x1="26" y1="34" x2="26" y2="74" stroke="#f7ede4" strokeOpacity="0.4" strokeWidth="1.5" />
      <line x1="26" y1="74" x2="96" y2="74" stroke="#f7ede4" strokeOpacity="0.4" strokeWidth="1.5" />
      <rect x="40" y="86" width="6" height="18" rx="2" fill="#6b5e58" />
      <rect x="74" y="86" width="6" height="18" rx="2" fill="#6b5e58" />
      <rect x="84" y="80" width="16" height="4" rx="2" fill={`url(#${id}-blush)`} />
    </Frame>
  );
};

// Two chat bubbles: a question and a typing reply.
const Chat = ({ className }) => {
  const id = "ill-chat";
  return (
    <Frame id={id} className={className}>
      <path d="M14 28 h58 a10 10 0 0 1 10 10 v20 a10 10 0 0 1 -10 10 h-38 l-12 10 v-10 h-8 a10 10 0 0 1 -10 -10 v-20 a10 10 0 0 1 10 -10 z" fill={`url(#${id}-ink)`} />
      <rect x="24" y="40" width="40" height="5" rx="2.5" fill="#f7ede4" opacity="0.7" />
      <rect x="24" y="51" width="26" height="5" rx="2.5" fill="#f7ede4" opacity="0.45" />
      <path d="M48 62 h48 a10 10 0 0 1 10 10 v14 a10 10 0 0 1 -10 10 h-2 v10 l-12 -10 h-34 a10 10 0 0 1 -10 -10 v-14 a10 10 0 0 1 10 -10 z" fill={`url(#${id}-pink)`} />
      <circle cx="62" cy="79" r="4" fill="#fff" opacity="0.95" />
      <circle cx="74" cy="79" r="4" fill="#fff" opacity="0.75" />
      <circle cx="86" cy="79" r="4" fill="#fff" opacity="0.55" />
    </Frame>
  );
};

// Stacked browser windows: frontend, API, database.
const Stack = ({ className }) => {
  const id = "ill-stack";
  const Window = ({ x, y, fill, bar }) => (
    <g>
      <rect x={x} y={y} width="66" height="46" rx="7" fill={fill} />
      <rect x={x} y={y} width="66" height="11" rx="7" fill={bar} />
      <circle cx={x + 8} cy={y + 5.5} r="2" fill="#f7ede4" />
      <circle cx={x + 15} cy={y + 5.5} r="2" fill="#f7ede4" opacity="0.7" />
    </g>
  );
  return (
    <Frame id={id} className={className}>
      <Window x={40} y={16} fill={`url(#${id}-blush)`} bar="#e3c3cc" />
      <Window x={27} y={38} fill={`url(#${id}-pink)`} bar="#d4386b" />
      <Window x={14} y={60} fill={`url(#${id}-ink)`} bar="#3b2a2f" />
      <path d="M26 84 l-6 6 6 6" fill="none" stroke="#ff8fb3" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" />
      <path d="M52 84 l6 6 -6 6" fill="none" stroke="#ff8fb3" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" />
      <line x1="42" y1="82" x2="36" y2="98" stroke="#f7ede4" strokeWidth="3" strokeLinecap="round" />
    </Frame>
  );
};

export const ILLUSTRATIONS = { network: Network, teach: Teach, chat: Chat, stack: Stack };
